'use client';

import { useCallback, useEffect, useState } from 'react';

import { useStudioHttpClient } from '@/providers/studio-api-provider';

export type SystemStorageListItem = {
  id?: string;
  publicUrl?: string;
  url?: string;
  fileName?: string;
  mimeType?: string;
};

export type UseStudioSystemStorageListParams = {
  from: number;
  size: number;
  storage?: string;
  regex?: string;
  enabled?: boolean;
};

function normalizeItem(item: SystemStorageListItem | string): SystemStorageListItem {
  if (typeof item === 'string') {
    return { publicUrl: item, url: item };
  }
  const url = item.publicUrl ?? item.url;
  return { ...item, publicUrl: url ?? undefined, url: url ?? undefined };
}

export function useStudioSystemStorageList(params: UseStudioSystemStorageListParams) {
  const { enabled = true, from, size, storage, regex } = params;
  const httpClient = useStudioHttpClient();
  const [data, setData] = useState<{ items: SystemStorageListItem[]; total: number } | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<unknown>();

  const refetch = useCallback(async () => {
    if (!enabled) {
      setData(undefined);
      return undefined;
    }

    setIsLoading(true);
    setIsError(false);
    setError(undefined);

    try {
      const res = await httpClient.get<
        | { items?: (SystemStorageListItem | string)[]; total?: number }
        | (SystemStorageListItem | string)[]
      >(`/v1/system/storage/list/${from}/${size}`, { params: { storage, regex } });
      const responseData = res.data;
      const rawItems = Array.isArray(responseData) ? responseData : (responseData.items ?? []);
      const items = rawItems
        .map(normalizeItem)
        .filter((item): item is SystemStorageListItem => !!(item.publicUrl ?? item.url));
      const total =
        typeof (responseData as { total?: number }).total === 'number'
          ? (responseData as { total: number }).total
          : items.length;
      const nextData = { items, total };
      setData(nextData);
      return nextData;
    } catch (nextError) {
      setIsError(true);
      setError(nextError);
      setData(undefined);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, [enabled, from, httpClient, regex, size, storage]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
  };
}
